// src/components/Billing/AccountBalanceButton.js - Plan-based model (Free / Vendor / Vendor+)

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { DollarSign, AlertCircle, CheckCircle, ArrowUpCircle, Star } from 'lucide-react';
import {
  getPlanFromBilling,
  isMonthlyDue,
  getMonthlyAmount,
  formatCurrency,
  PLANS,
  PLAN_ORDER,
} from '../../utils/billingTiers';

const BalanceButton = styled.button`
  background: ${props => {
    if (props.$overdue) return 'linear-gradient(135deg, #f59e0b, #d97706)';
    if (props.$upgrade) return 'linear-gradient(135deg, #8b5cf6, #7c3aed)';
    if (props.$start)   return 'linear-gradient(135deg, #3b82f6, #2563eb)';
    return 'linear-gradient(135deg, #10b981, #059669)';
  }};
  color: white;
  border: none;
  padding: 0.55rem 1.35rem;
  border-radius: 50px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 700;
  transition: all 0.2s ease;
  box-shadow: ${props => {
    if (props.$overdue) return '0 3px 12px rgba(245,158,11,0.4)';
    if (props.$upgrade) return '0 3px 12px rgba(139,92,246,0.4)';
    if (props.$start)   return '0 3px 12px rgba(59,130,246,0.4)';
    return '0 3px 12px rgba(16,185,129,0.3)';
  }};
  font-size: 0.82rem;
  letter-spacing: 0.03em;

  &:hover:not(:disabled) {
    transform: scale(1.02);
    opacity: 0.92;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BadgeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  margin: 0.25rem 0 0;
`;

const PlanBadge = styled.div`
  background: ${props => `${props.$color}18`};
  border: 1px solid ${props => `${props.$color}50`};
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${props => props.$color};
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const PLAN_COLORS = { ...Object.fromEntries(Object.entries(PLANS).map(([key, p]) => [key, p.color])), vendor: '#3b82f6', vendor_plus: '#8b5cf6' };
const TOP_PLAN = PLAN_ORDER[PLAN_ORDER.length - 1];
const UPGRADEABLE_PLANS = new Set(['free', 'starter', 'vendor', 'pro', 'vendor_plus', 'elite']);

const AccountBalanceButton = ({ userId, shopData, billingData: billingDataProp, onOpenPayment, theme }) => {
  const [fetchedBilling, setFetchedBilling] = useState(null);
  const [loading, setLoading] = useState(!billingDataProp);
  const lastLoadTime = useRef(0);
  const lastLoadedUserId = useRef(null);

  // Only fetch from Firestore when no billingData prop is provided
  useEffect(() => {
    if (billingDataProp) {
      setLoading(false);
      return;
    }

    const load = async () => {
      if (!userId) return;

      const userChanged = lastLoadedUserId.current !== userId;
      if (userChanged) {
        lastLoadTime.current = 0;
        lastLoadedUserId.current = userId;
      }

      const now = Date.now();
      if (!userChanged && now - lastLoadTime.current < 30000) return;
      lastLoadTime.current = now;

      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../../firebase/config');

        const billingRef = doc(db, 'billing', userId);
        const billingDoc = await getDoc(billingRef);

        const billing = billingDoc.exists()
          ? billingDoc.data()
          : { userId, plan: 'free', itemsHiddenDueToBalance: false };

        setFetchedBilling(billing);
      } catch (err) {
        console.error('AccountBalanceButton: billing load error', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [userId, shopData?.totalSales, billingDataProp]);

  const billingData = billingDataProp || fetchedBilling;

  if (loading) {
    return (
      <BadgeContainer>
        <PlanBadge $color="#6b7280">Loading...</PlanBadge>
      </BadgeContainer>
    );
  }

  if (!billingData) return null;

  const plan      = getPlanFromBilling(billingData);
  const planLabel = PLANS[plan]?.label || 'Free';
  const planColor = PLAN_COLORS[plan] || '#6b7280';
  const overdue   = isMonthlyDue(billingData);
  const monthlyAmt = getMonthlyAmount(billingData);

  // ── Determine button state ────────────────────────────────────────────────
  const isStart   = plan === 'free';
  const isUpgrade = !isStart && !overdue && UPGRADEABLE_PLANS.has(plan) && plan !== TOP_PLAN;
  const isOverdue = overdue;

  const handleClick = () => {
    if (onOpenPayment) onOpenPayment({ ...billingData, plan, openFrom: isStart ? 'start' : isUpgrade ? 'upgrade' : 'payment' });
  };

  const buttonLabel = () => {
    if (isOverdue)  return `${formatCurrency(monthlyAmt)} Due`;
    if (isStart)    return 'Start Selling';
    if (isUpgrade)  return 'Upgrade Plan';
    return 'Active';
  };

  const ButtonIcon = () => {
    if (isOverdue)  return <AlertCircle size={15} />;
    if (isStart)    return <Star size={15} />;
    if (isUpgrade)  return <ArrowUpCircle size={15} />;
    return <CheckCircle size={15} />;
  };

  return (
    <BadgeContainer>
      <BalanceButton
        $overdue={isOverdue}
        $start={isStart}
        $upgrade={isUpgrade}
        onClick={handleClick}
        disabled={loading}
      >
        <ButtonIcon />
        <DollarSign size={14} />
        <span>{buttonLabel()}</span>
      </BalanceButton>

      <PlanBadge $color={planColor}>
        {planLabel} Plan
        {!isStart && !isOverdue && ` · ${formatCurrency(billingData.totalSales || 0)} Earned`}
      </PlanBadge>
    </BadgeContainer>
  );
};

export default AccountBalanceButton;
